import { FC } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { DragNDrop } from 'components/DragNDrop';
import { RadioField } from 'components/Forms/RadioField';
import { useFieldValue } from 'hooks/useFormHelpers';
import { getDuplicateTicketPrices } from 'views/Pages/utils';

interface DonationTicketFormProps {
  isPageDuplicated: boolean;
}

export const DonationTicketForm: FC<DonationTicketFormProps> = ({ isPageDuplicated }) => {
  const { change } = useForm();
  const ticketArrangement = useFieldValue('ticket_arrangement');
  const rows = useFieldValue('rows');

  const addRow = () => {
    change('rows', [
      ...rows,
      {
        id: (rows.length + 1).toString(),
        name: '',
        price: 0,
        quantity: 999,
        quantity_remaining: 999,
        status: 'ACTIVE',
        description: '',
        order: rows.length + 1,
      },
    ]);
  };

  const deleteRow = (ticketID: string) => {
    change(
      'rows',
      rows.map(row => (row.id === ticketID ? { ...row, status: 'REMOVED' } : row))
    );
  };

  const labels = [
    'Ticket Types',
    'Pricing per Ticket *',
    'Quantity *',
    'Caption Preview (optional)',
  ];

  const ticketArrangementOptions = [
    { label: 'Manually', value: 'MANUALLY' },
    { label: 'By Ticket Price', value: 'BY_PRICE' },
  ];

  const setRows = (newRows: any) => {
    // Preserve removed rows so the array never shrinks & IDs don't collide.
    const nonActiveRows = Array.isArray(rows) ? rows.filter(row => row.status !== 'ACTIVE') : [];
    change('rows', [...newRows, ...nonActiveRows]);
  };

  const { errors } = useFormState({ subscription: { errors: true } });

  const activeRows = Array.isArray(rows) ? rows.filter(row => row.status === 'ACTIVE') : [];
  const duplicatePrices = getDuplicateTicketPrices(rows);

  return (
    <div className="form-group">
      <label htmlFor="donation-tickets">
        Add and edit your ticket options below. Minimum of 1 ticket type required for an event.
      </label>
      {rows && (
        <DragNDrop
          rows={activeRows}
          setRows={setRows}
          labels={labels}
          deleteRow={deleteRow}
          deleteText="Delete Ticket"
          isPageDuplicated={isPageDuplicated}
          priceHasError={row => duplicatePrices.has(row.price)}
        />
      )}
      {errors?.rows && (
        <div className="form-group has-danger">
          <label className="error" htmlFor="donation-tickets">
            {errors.rows}
          </label>
        </div>
      )}
      <button type="button" className="oath-bodysmall link-text gray padding0" onClick={addRow}>
        Add Ticket Type
      </button>
      <div className="margin0 form-group col-md-6 padding0">
        <RadioField
          name="ticket_arrangement"
          label="Ticket Arrangement"
          options={ticketArrangementOptions}
        />
        {ticketArrangement === 'MANUALLY' && (
          <label htmlFor="ticket-arrangement">
            Drag and drop ticket types in the order you would like them to appear on the screen,
            from top to bottom.
          </label>
        )}
      </div>
    </div>
  );
};
